package org.example.project

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.example.project.GetOrgInfoResult
import org.example.project.components.*

enum class SettingsTab(val title: String) {
    OVERVIEW("Overview"),
    CODES("Licenses"),
    TRANSFER("Provisioning"),
    ADMIN("Admin")
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsPage(
    userEmail: String?,
    onBackClick: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val viewModel = remember { SettingsViewModel(scope) }
    
    val currentUserDoc by FirebaseManager.currentUserDoc.collectAsState()
    val currentSystemProfile by FirebaseManager.currentSystemProfile.collectAsState()
    
    val balances = currentUserDoc?.balances
    val userPoints = (balances?.iap_units ?: 0) + (balances?.bonus_units ?: 0)
    val isAdmin = currentSystemProfile?.role == UserRoleType.ADMIN

    val taxCountriesList by viewModel.taxCountriesList.collectAsState()
    val fiatPaymentMethodsList by viewModel.fiatPaymentMethodsList.collectAsState()
    val licensePackages by viewModel.licensePackages.collectAsState()
    val orgLevels by viewModel.orgLevels.collectAsState()
    val licenseKeys by viewModel.licenseKeys.collectAsState()
    val suggestedLicenseUnits by viewModel.suggestedLicenseUnits.collectAsState()

    var queriedSystemProfile by remember { mutableStateOf<SystemProfileDocument?>(null) }

    LaunchedEffect(currentUserDoc != null) {
        if (currentUserDoc != null) {
            viewModel.loadOrgData()
        }
    }

    val tabs = remember(isAdmin) {
        val list = mutableListOf(SettingsTab.OVERVIEW, SettingsTab.CODES, SettingsTab.TRANSFER)
        if (isAdmin) list.add(SettingsTab.ADMIN)
        list
    }
    var selectedTabIndex by remember { mutableStateOf(0) }
    if (selectedTabIndex >= tabs.size) {
        selectedTabIndex = 0
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Settings", fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.White
                )
            )
        }
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .background(BackgroundColor)
        ) {
            ScrollableTabRow(
                selectedTabIndex = selectedTabIndex,
                containerColor = Color.White,
                contentColor = PrimaryColor,
                edgePadding = 16.dp,
                divider = { HorizontalDivider(color = Color(0xFFF1F5F9)) }
            ) {
                tabs.forEachIndexed { index, tab ->
                    Tab(
                        selected = selectedTabIndex == index,
                        onClick = { selectedTabIndex = index },
                        text = { Text(tab.title, fontWeight = FontWeight.Bold) },
                        selectedContentColor = PrimaryColor,
                        unselectedContentColor = Color(0xFF64748B) // Slate 500
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(24.dp),
                verticalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                when (tabs[selectedTabIndex]) {
                    SettingsTab.OVERVIEW -> {
                        AccountInfoSection(
                            iapUnits = balances?.iap_units ?: 0,
                            orgLicenseUnits = balances?.bonus_units ?: 0
                        )
                        
                        // 如果当前用户是管理员，并且在 Admin 面板查询了其他机构的账号，则概览信息显示被查询机构的等级和包信息；否则显示当前用户自己的信息
                        val targetSystemProfile = if (isAdmin && queriedSystemProfile != null) queriedSystemProfile else currentSystemProfile

                        LevelInfoSection(
                            currentSystemProfile = targetSystemProfile,
                            orgLevels = orgLevels
                        )
                        LicensePackagesSection(
                            licensePackages = licensePackages,
                            currentUserLevel = targetSystemProfile?.org_metadata?.level ?: 0
                        )
                    }
                    SettingsTab.CODES -> {
                        CheckLicenseKeySection()

                        LicenseKeySection()
                        
                        CreateLicenseKeysSection(
                            suggestedLicenseUnits = suggestedLicenseUnits,
                            userPoints = userPoints,
                            onCodesCreated = { viewModel.loadLicenseKeys(isRefresh = true) }
                        )
                        LicenseKeyListSection(
                            licenseKeys = licenseKeys,
                            isLoadingCodes = viewModel.isLoadingCodes.value,
                            nextCursor = viewModel.nextCursor.value,
                            selectedStatus = viewModel.selectedStatus.value,
                            onStatusChange = { viewModel.onStatusFilterChanged(it) },
                            onLoadCodes = { isRefresh -> viewModel.loadLicenseKeys(isRefresh) }
                        )
                    }
                    SettingsTab.TRANSFER -> {
                        ProvisionLicensesSection(
                            currentSystemProfile = currentSystemProfile,
                            taxCountriesList = taxCountriesList,
                            fiatPaymentMethodsList = fiatPaymentMethodsList
                        )
                    }
                    SettingsTab.ADMIN -> {
                        QueryOrgSection(
                            onResult = { doc -> queriedSystemProfile = doc }
                        )
                        EnableOrgSection()
                        ManageTaxInfoSection(taxCountriesList = taxCountriesList)
                        DeductLicenseUnitsSection()
                    }
                }
            }
        }
    }
}
